import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  GraduationCap,
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Mail,
  FileText,
  Calendar,
  Shield,
  BookOpen,
  Award,
  TrendingUp,
  ChevronRight,
  Eye,
  Send,
  Filter,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrainingManagementPanel() {
  const { getAuthHeaders } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [users, setUsers] = useState([]);
  const [userCompliance, setUserCompliance] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  
  // Modal states
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [showUserComplianceModal, setShowUserComplianceModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state for new/edit requirement
  const [requirementForm, setRequirementForm] = useState({
    name: '',
    description: '',
    training_type: 'system',
    required_for_roles: [],
    validity_period_days: 365,
    is_mandatory: true,
    study_specific: false
  });
  
  // Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    compliantUsers: 0,
    nonCompliantUsers: 0,
    expiringSoon: 0,
    totalRequirements: 0
  });

  // Available roles for filtering
  const roles = [
    { value: 'system_admin', label: 'System Administrator' },
    { value: 'tmf_lead', label: 'TMF Lead / Manager' },
    { value: 'ctm', label: 'Clinical Trial Manager' },
    { value: 'cra', label: 'CRA / Monitor' },
    { value: 'cdm', label: 'Clinical Document Manager' },
    { value: 'qc_reviewer', label: 'QC Reviewer' },
    { value: 'qc_auditor', label: 'QC Auditor' },
    { value: 'qa_oversight', label: 'QA / Compliance' },
    { value: 'regulatory', label: 'Regulatory Affairs' },
    { value: 'site_user', label: 'Site User' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'inspector', label: 'Inspector' }
  ];

  const trainingTypes = [
    { value: 'gcp', label: 'GCP Training' },
    { value: 'regulatory', label: 'Regulatory' },
    { value: 'system', label: 'System Training' },
    { value: 'sop', label: 'SOP' },
    { value: 'role_specific', label: 'Role Specific' }
  ];

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/training/requirements`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/users`, getAuthHeaders())
      ]);
      
      setRequirements(reqRes.data.requirements || []);
      
      // Handle both array and object response formats for users
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.users || []);
      setUsers(usersData);
      
      // Fetch compliance for each user
      const complianceData = [];
      for (const user of usersData.slice(0, 50)) { // Limit to first 50 users
        try {
          const compRes = await axios.get(
            `${API_URL}/api/training/user/${user.id}/compliance`,
            getAuthHeaders()
          );
          complianceData.push({
            ...user,
            compliance: compRes.data.compliance_status,
            compliant_items: compRes.data.compliant_items,
            non_compliant_items: compRes.data.non_compliant_items,
            expiring_soon: compRes.data.expiring_soon
          });
        } catch (err) {
          complianceData.push({
            ...user,
            compliance: { is_fully_compliant: false, compliance_percentage: 0 },
            compliant_items: [],
            non_compliant_items: [],
            expiring_soon: []
          });
        }
      }
      
      setUserCompliance(complianceData);
      
      // Calculate stats
      const compliant = complianceData.filter(u => u.compliance?.is_fully_compliant).length;
      const expiring = complianceData.filter(u => (u.expiring_soon?.length || 0) > 0).length;
      
      setStats({
        totalUsers: complianceData.length,
        compliantUsers: compliant,
        nonCompliantUsers: complianceData.length - compliant,
        expiringSoon: expiring,
        totalRequirements: reqRes.data.requirements?.length || 0
      });
      
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create/Update requirement
  const handleSaveRequirement = async () => {
    try {
      if (isEditing && selectedRequirement) {
        await axios.put(
          `${API_URL}/api/training/requirements/${selectedRequirement.id}`,
          requirementForm,
          getAuthHeaders()
        );
        toast.success('Training requirement updated');
      } else {
        await axios.post(
          `${API_URL}/api/training/requirements`,
          requirementForm,
          getAuthHeaders()
        );
        toast.success('Training requirement created');
      }
      setShowRequirementModal(false);
      resetRequirementForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save requirement');
    }
  };

  // Delete requirement
  const handleDeleteRequirement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training requirement?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/training/requirements/${id}`,
        getAuthHeaders()
      );
      toast.success('Training requirement deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete requirement');
    }
  };

  // Reset form
  const resetRequirementForm = () => {
    setRequirementForm({
      name: '',
      description: '',
      training_type: 'system',
      required_for_roles: [],
      validity_period_days: 365,
      is_mandatory: true,
      study_specific: false
    });
    setSelectedRequirement(null);
    setIsEditing(false);
  };

  // Open edit modal
  const openEditModal = (requirement) => {
    setSelectedRequirement(requirement);
    setRequirementForm({
      name: requirement.name,
      description: requirement.description,
      training_type: requirement.training_type,
      required_for_roles: requirement.required_for_roles || [],
      validity_period_days: requirement.validity_period_days || 365,
      is_mandatory: requirement.is_mandatory !== false,
      study_specific: requirement.study_specific || false
    });
    setIsEditing(true);
    setShowRequirementModal(true);
  };

  // View user compliance details
  const viewUserCompliance = (user) => {
    setSelectedUser(user);
    setShowUserComplianceModal(true);
  };

  // Filter users
  const filteredUsers = userCompliance.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    const matchesCompliance = complianceFilter === 'all' ||
      (complianceFilter === 'compliant' && user.compliance?.is_fully_compliant) ||
      (complianceFilter === 'non-compliant' && !user.compliance?.is_fully_compliant) ||
      (complianceFilter === 'expiring' && (user.expiring_soon?.length || 0) > 0);
    
    return matchesSearch && matchesRole && matchesCompliance;
  });

  // Get compliance badge
  const getComplianceBadge = (user) => {
    if (user.compliance?.is_fully_compliant) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Compliant</Badge>;
    }
    if ((user.expiring_soon?.length || 0) > 0) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Expiring Soon</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700 border-red-200">Non-Compliant</Badge>;
  };

  // Get training type badge color
  const getTypeBadgeColor = (type) => {
    const colors = {
      'gcp': 'bg-blue-100 text-blue-700',
      'regulatory': 'bg-purple-100 text-purple-700',
      'system': 'bg-teal-100 text-teal-700',
      'sop': 'bg-orange-100 text-orange-700',
      'role_specific': 'bg-pink-100 text-pink-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  // Send training reminder email
  const sendReminder = async (user) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/training/send-reminder`,
        { user_id: user.id },
        getAuthHeaders()
      );
      
      if (response.data.email_sent) {
        toast.success(`Training reminder sent to ${user.email}`);
      } else {
        toast.info(response.data.message || 'User has no pending training');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  // Send bulk reminders to all non-compliant users
  const sendBulkReminders = async () => {
    if (!window.confirm('Send training reminders to all non-compliant users?')) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/training/send-bulk-reminders`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`Sent ${response.data.sent} reminders, ${response.data.skipped_compliant} users already compliant`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk reminders');
    }
  };

  // Export compliance report
  const exportComplianceReport = () => {
    const report = filteredUsers.map(user => ({
      Name: user.name,
      Email: user.email,
      Role: roles.find(r => r.value === user.role)?.label || user.role,
      'Compliance %': user.compliance?.compliance_percentage || 0,
      'Compliant': user.compliance?.is_fully_compliant ? 'Yes' : 'No',
      'Pending Items': user.non_compliant_items?.length || 0,
      'Expiring Soon': user.expiring_soon?.length || 0
    }));
    
    // Convert to CSV
    const headers = Object.keys(report[0] || {}).join(',');
    const rows = report.map(row => Object.values(row).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Compliance report exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-red-600" />
            Training Management
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage training requirements and monitor user compliance
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.totalUsers}</p>
                <p className="text-xs text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.compliantUsers}</p>
                <p className="text-xs text-slate-500">Compliant</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.nonCompliantUsers}</p>
                <p className="text-xs text-slate-500">Non-Compliant</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.expiringSoon}</p>
                <p className="text-xs text-slate-500">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.totalRequirements}</p>
                <p className="text-xs text-slate-500">Requirements</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            User Compliance
          </TabsTrigger>
          <TabsTrigger value="requirements" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            <BookOpen className="w-4 h-4 mr-2" />
            Requirements
          </TabsTrigger>
        </TabsList>

        {/* User Compliance Tab */}
        <TabsContent value="overview" className="mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-800"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-44 bg-white dark:bg-slate-800">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map(role => (
                  <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={complianceFilter} onValueChange={setComplianceFilter}>
              <SelectTrigger className="w-full sm:w-44 bg-white dark:bg-slate-800">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="compliant">Compliant</SelectItem>
                <SelectItem value="non-compliant">Non-Compliant</SelectItem>
                <SelectItem value="expiring">Expiring Soon</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={exportComplianceReport} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            {stats.nonCompliantUsers > 0 && (
              <Button onClick={sendBulkReminders} variant="default" size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Mail className="w-4 h-4 mr-2" />
                Send All Reminders
              </Button>
            )}
          </div>

          {/* Users Table */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <ScrollArea className="h-[500px]">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900 z-10">
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">User</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Role</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Compliance</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Progress</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr 
                      key={user.id} 
                      className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30"
                    >
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs">
                          {roles.find(r => r.value === user.role)?.label || user.role}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {getComplianceBadge(user)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={user.compliance?.compliance_percentage || 0} 
                            className="w-24 h-2"
                          />
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {user.compliance?.compliance_percentage || 0}%
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewUserCompliance(user)}
                            className="h-8"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {!user.compliance?.is_fully_compliant && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => sendReminder(user)}
                              className="h-8 text-amber-600 hover:text-amber-700"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-500">
                        No users found matching your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* Requirements Tab */}
        <TabsContent value="requirements" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-slate-600 dark:text-slate-400">
              {requirements.length} training requirement(s) configured
            </p>
            <Button onClick={() => { resetRequirementForm(); setShowRequirementModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Requirement
            </Button>
          </div>

          <div className="grid gap-4">
            {requirements.map(req => (
              <Card key={req.id} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{req.name}</h3>
                        <Badge className={getTypeBadgeColor(req.training_type)}>
                          {trainingTypes.find(t => t.value === req.training_type)?.label || req.training_type}
                        </Badge>
                        {req.is_mandatory && (
                          <Badge variant="outline" className="border-red-200 text-red-600">
                            Mandatory
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                        {req.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {req.required_for_roles?.slice(0, 5).map(role => (
                          <Badge key={role} variant="secondary" className="text-xs">
                            {roles.find(r => r.value === role)?.label || role}
                          </Badge>
                        ))}
                        {(req.required_for_roles?.length || 0) > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{req.required_for_roles.length - 5} more
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Valid for {req.validity_period_days || 365} days
                        </span>
                        {req.study_specific && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Study-specific
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(req)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteRequirement(req.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Requirement Modal */}
      <Dialog open={showRequirementModal} onOpenChange={setShowRequirementModal}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">
              {isEditing ? 'Edit Training Requirement' : 'Add Training Requirement'}
            </DialogTitle>
            <DialogDescription>
              Configure the training requirement details and applicable roles.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={requirementForm.name}
                onChange={(e) => setRequirementForm({ ...requirementForm, name: e.target.value })}
                placeholder="e.g., GCP Fundamentals"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                value={requirementForm.description}
                onChange={(e) => setRequirementForm({ ...requirementForm, description: e.target.value })}
                placeholder="Training description..."
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Training Type</Label>
                <Select 
                  value={requirementForm.training_type}
                  onValueChange={(v) => setRequirementForm({ ...requirementForm, training_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {trainingTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  value={requirementForm.validity_period_days}
                  onChange={(e) => setRequirementForm({ ...requirementForm, validity_period_days: parseInt(e.target.value) || 365 })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Required for Roles</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                {roles.map(role => (
                  <label key={role.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={requirementForm.required_for_roles.includes(role.value)}
                      onChange={(e) => {
                        const newRoles = e.target.checked
                          ? [...requirementForm.required_for_roles, role.value]
                          : requirementForm.required_for_roles.filter(r => r !== role.value);
                        setRequirementForm({ ...requirementForm, required_for_roles: newRoles });
                      }}
                      className="rounded text-red-600"
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requirementForm.is_mandatory}
                  onChange={(e) => setRequirementForm({ ...requirementForm, is_mandatory: e.target.checked })}
                  className="rounded text-red-600"
                />
                Mandatory
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requirementForm.study_specific}
                  onChange={(e) => setRequirementForm({ ...requirementForm, study_specific: e.target.checked })}
                  className="rounded text-red-600"
                />
                Study-specific
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequirementModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRequirement} disabled={!requirementForm.name || !requirementForm.description}>
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Compliance Details Modal */}
      <Dialog open={showUserComplianceModal} onOpenChange={setShowUserComplianceModal}>
        <DialogContent className="max-w-2xl bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-red-600" />
              Training Compliance - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.email} • {roles.find(r => r.value === selectedUser?.role)?.label}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4 space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedUser.compliance?.is_fully_compliant ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-medium text-slate-900 dark:text-white">
                      {selectedUser.compliance?.is_fully_compliant ? 'Fully Compliant' : 'Action Required'}
                    </span>
                  </div>
                  <Progress 
                    value={selectedUser.compliance?.compliance_percentage || 0} 
                    className="h-3"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedUser.compliance?.compliance_percentage || 0}% complete • 
                    {selectedUser.compliant_items?.length || 0} completed, 
                    {selectedUser.non_compliant_items?.length || 0} pending
                  </p>
                </div>
              </div>

              {/* Expiring Soon */}
              {(selectedUser.expiring_soon?.length || 0) > 0 && (
                <div>
                  <h4 className="font-medium text-amber-600 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Expiring Soon ({selectedUser.expiring_soon.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedUser.expiring_soon.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                        <span className="text-sm text-slate-700 dark:text-slate-300">{item.requirement_name}</span>
                        <span className="text-xs text-amber-600">{item.days_until_expiry} days left</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Training */}
              {(selectedUser.non_compliant_items?.length || 0) > 0 && (
                <div>
                  <h4 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Pending Training ({selectedUser.non_compliant_items.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedUser.non_compliant_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded">
                        <div>
                          <span className="text-sm text-slate-700 dark:text-slate-300">{item.requirement_name}</span>
                          <Badge className="ml-2 text-xs" variant="outline">
                            {item.status === 'expired' ? 'Expired' : 'Not Started'}
                          </Badge>
                        </div>
                        <Badge className={`text-xs ${item.is_mandatory ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {item.is_mandatory ? 'Mandatory' : 'Optional'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Training */}
              {(selectedUser.compliant_items?.length || 0) > 0 && (
                <div>
                  <h4 className="font-medium text-green-600 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Completed ({selectedUser.compliant_items.length})
                  </h4>
                  <ScrollArea className="h-40">
                    <div className="space-y-2">
                      {selectedUser.compliant_items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
                          <span className="text-sm text-slate-700 dark:text-slate-300">{item.requirement_name}</span>
                          <div className="flex items-center gap-2">
                            {item.verified && (
                              <Shield className="w-4 h-4 text-green-600" title="Verified" />
                            )}
                            {item.expires_at && (
                              <span className="text-xs text-slate-500">
                                Expires: {new Date(item.expires_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {selectedUser && !selectedUser.compliance?.is_fully_compliant && (
              <Button variant="outline" onClick={() => sendReminder(selectedUser)}>
                <Send className="w-4 h-4 mr-2" />
                Send Reminder
              </Button>
            )}
            <Button onClick={() => setShowUserComplianceModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
